import { useCallback, useMemo, useState } from 'react'
import { Trans } from 'react-i18next'
import { Active, closestCenter, DndContext, Over } from '@dnd-kit/core'
import { restrictToFirstScrollableAncestor } from '@dnd-kit/modifiers'
import { arrayMove, SortableContext } from '@dnd-kit/sortable'
import clsx from 'clsx'

import { Plus16 } from '@makyoapp/makyo-icon/icon'
import { Button, Divider } from '@makyoapp/makyo-ui'

import { useAppPagesContext } from '@/context/AppPagesContext'
import { useSortableHandle } from '@/hooks/useSortableHandle'

import { SearchField } from './SearchField'
import SortablePage from './SortablePage'

function PagePanelNormal() {
  const [simpleEdit, setSimpleEdit] = useState('')
  const [searchData, setSearchData] = useState('')

  const { pages, addPage, isPageLoading, reorderPage } = useAppPagesContext()

  const extractTextFromTitle = useCallback((titleJson: string | null | undefined): string => {
    if (!titleJson || typeof titleJson !== 'string') return ''

    try {
      const parsed = JSON.parse(titleJson)
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed[0]?.children?.[0]?.text || ''
      }
      return ''
    } catch (error) {
      console.warn('Failed to parse title JSON:', error)
      return ''
    }
  }, [])

  const filteredPages = useMemo(() => {
    if (!pages) return []

    if (!searchData.trim()) return pages

    const searchTerm = searchData.toLowerCase().trim()

    return pages.filter((page) => {
      if (!page) return false

      const titleText = extractTextFromTitle(page.title).toLowerCase()
      const slugText = (page.slug || '').toLowerCase()

      return titleText.includes(searchTerm) || slugText.includes(searchTerm)
    })
  }, [pages, searchData, extractTextFromTitle])

  const dragEndCb = useCallback(
    async (active: Active, over: Over) => {
      if (!active.id || !over.id) return

      await reorderPage((currentPages) => {
        const oldIndex = currentPages.findIndex((p) => p.id === active.id)
        const newIndex = currentPages.findIndex((p) => p.id === over.id)

        if (oldIndex === -1 || newIndex === -1) return currentPages

        return arrayMove(currentPages, oldIndex, newIndex)
      })
    },
    [reorderPage]
  )

  const { sensors, handleDragEnd } = useSortableHandle(dragEndCb)

  const handleAddPage = useCallback(async () => {
    await addPage()
  }, [addPage])

  return (
    <div className='p-4'>
      <div className={clsx('page-list flex flex-col pb-4')}>
        <div className='uppercase py-2'>
          <Trans>Screens</Trans>
        </div>
        {pages?.length > 0 && <SearchField search={searchData} setSearch={setSearchData} className='-mx-4' />}
        <div className='flex flex-col gap-1'>
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
            modifiers={[restrictToFirstScrollableAncestor]}
          >
            <SortableContext items={filteredPages}>
              {filteredPages.map((page, index) => {
                if (!page || page.tableId) return null

                return (
                  <SortablePage
                    isEdit={simpleEdit === page.id}
                    page={page}
                    key={page.id}
                    index={index}
                    onFinishEdit={() => {
                      setSimpleEdit('')
                    }}
                  />
                )
              })}
            </SortableContext>
          </DndContext>
        </div>
      </div>
      <Button
        disabled={isPageLoading}
        icon={isPageLoading ? undefined : <Plus16 />}
        onClick={handleAddPage}
        className='w-full'
      >
        {isPageLoading ? <span className='loading spin' /> : <Trans>New Screen</Trans>}
      </Button>
      <Divider />
    </div>
  )
}

export default PagePanelNormal
